/**
 * Client-side validation for custom ArcGIS FeatureServer / MapServer
 * query URLs. Mirrors the subset of `server.ts` `ALLOWED_PROXY_HOSTS`
 * relevant to ArcGIS regional layer fetch — keep in sync on host changes.
 */

/** ArcGIS-family hosts already allowlisted in `server.ts`. */
const ALLOWED_ARCGIS_HOSTS = new Set([
  'services1.arcgis.com',
  'services5.arcgis.com',
  'services.arcgis.com',
  'services2.arcgis.com',
  'services9.arcgis.com',
  'biamaps.geoplatform.gov',
]);

const FEATURE_SERVER_PATH =
  /\/(?:FeatureServer|MapServer)\/\d+\/query\/?$/i;

const IPV4 = /^\d{1,3}(\.\d{1,3}){3}$/;
const IPV6 = /^[\da-f:]+$/i;

export interface ValidateArcgisQueryUrlResult {
  ok: boolean;
  /** Normalised base query URL (no search params). */
  normalizedUrl?: string;
  error?: string;
}

export function validateArcgisQueryUrl(raw: string): ValidateArcgisQueryUrlResult {
  const trimmed = raw.trim();
  if (!trimmed) {
    return { ok: false, error: 'URL is required' };
  }

  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return { ok: false, error: 'Invalid URL format' };
  }

  if (parsed.protocol !== 'https:') {
    return { ok: false, error: 'Only HTTPS URLs are allowed' };
  }

  const host = parsed.hostname.toLowerCase();
  if (IPV4.test(host) || (host.includes(':') && IPV6.test(host))) {
    return { ok: false, error: 'IP literal hosts are not allowed' };
  }

  if (!ALLOWED_ARCGIS_HOSTS.has(host)) {
    return { ok: false, error: `Host "${host}" is not on the allowlist` };
  }

  const path = parsed.pathname.replace(/\/+$/, '');
  if (!FEATURE_SERVER_PATH.test(path)) {
    return {
      ok: false,
      error: 'URL must end with /FeatureServer/N/query or /MapServer/N/query',
    };
  }

  const normalizedUrl = `${parsed.origin}${path}`;
  return { ok: true, normalizedUrl };
}
